import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { StatusBadge, StatTile, Overline } from "@/components/ui-bits";
import { toast } from "sonner";
import { Wallet, Check, Ban, Save, TrendingUp, TrendingDown, ArrowDownCircle, ArrowUpCircle, Coins, Lock, ShieldCheck, ShieldAlert, Link2 } from "lucide-react";

export default function Treasury() {
  const { ws, reloadWs, user } = useOutletContext();
  const isAdmin = user?.role === "admin";
  const [ledger, setLedger] = useState([]);
  const [pending, setPending] = useState([]);
  const [policy, setPolicy] = useState(null);
  const [integrity, setIntegrity] = useState(null);

  const load = () => {
    api.get("/treasury/ledger").then((r) => setLedger(r.data)).catch(() => {});
    api.get("/spends?status=pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/treasury/integrity").then((r) => setIntegrity(r.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 12000);
    return () => clearInterval(i);
  }, []);
  useRealtime(() => load());
  useEffect(() => { if (ws && !policy) setPolicy({ approval_threshold: ws.approval_threshold, daily_cap: ws.daily_cap, monthly_cap: ws.monthly_cap, deploy_requires_approval: ws.deploy_requires_approval !== false, demo_mode: ws.demo_mode !== false }); }, [ws]);

  const savePolicy = async (force = false) => {
    try {
      await api.patch("/workspace", { approval_threshold: Number(policy.approval_threshold), daily_cap: Number(policy.daily_cap), monthly_cap: Number(policy.monthly_cap), deploy_requires_approval: !!policy.deploy_requires_approval, demo_mode: !!policy.demo_mode, ...(force ? { force_demo_off: true } : {}) });
      toast.success("Approval policy updated");
      reloadWs();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "go_live_incomplete") {
        const proceed = window.confirm(`${d.message}\n\nStill incomplete:\n• ${d.missing.join("\n• ")}\n\nTurn off demo mode anyway?`);
        if (proceed) return savePolicy(true);
        setPolicy({ ...policy, demo_mode: true });
      } else { toast.error("Failed to update policy"); }
    }
  };

  const resetDemo = async () => {
    if (!window.confirm("Wipe ALL demo data (orders, payments, ledger, deployments, inbox) and reset balances to $0? Agents and team are kept. This cannot be undone.")) return;
    try {
      const { data } = await api.post("/demo/reset");
      const n = Object.values(data.cleared || {}).reduce((a, b) => a + b, 0);
      toast.success(`Demo data cleared — ${n} records wiped, balances reset to $0`);
      load();
      reloadWs();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const resolve = async (sp, approve) => {
    try {
      await api.post(`/spends/${sp.id}/resolve`, { approve });
      toast[approve ? "success" : "error"](approve ? `Approved ${money(sp.amount)}` : `Rejected ${money(sp.amount)}`);
      load();
      reloadWs();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const ICON = { revenue: ArrowDownCircle, funding: Coins, spend: ArrowUpCircle, refund: ArrowDownCircle };
  const COLOR = { revenue: "text-emerald-400", funding: "text-cyan-400", spend: "text-amber-400", refund: "text-cyan-400" };

  return (
    <div className="space-y-6" data-testid="treasury-page">
      <div>
        <Overline>Treasury & Policy Spend Approvals</Overline>
        <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">Treasury & Wallets</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatTile testid="treasury-balance" label="Treasury Balance" value={money(ws?.treasury_balance)} accent="emerald" icon={Wallet} />
        <StatTile testid="treasury-revenue" label="Total Revenue" value={money(ws?.total_revenue)} accent="cyan" icon={TrendingUp} />
        <StatTile testid="treasury-spend" label="Total Spend" value={money(ws?.total_spend)} accent="amber" icon={TrendingDown} />
      </div>

      {integrity && (
        <div className={`hud-card accent-top rounded-lg p-4 flex flex-wrap items-center gap-x-6 gap-y-2`} style={{ "--accent-line": integrity.reconciled && integrity.chain_valid ? "#10B981" : "#EF4444" }} data-testid="integrity-card">
          <div className="flex items-center gap-2">
            {integrity.reconciled && integrity.chain_valid
              ? <ShieldCheck className="w-5 h-5 text-emerald-400" />
              : <ShieldAlert className="w-5 h-5 text-red-400" />}
            <span className="font-display font-bold text-sm">Ledger Integrity</span>
          </div>
          <Pill ok={integrity.chain_valid} label={integrity.chain_valid ? "Hash chain verified" : "Chain tampered"} icon={Link2} testid="integrity-chain" />
          <Pill ok={integrity.reconciled} label={integrity.reconciled ? "Books reconciled" : `${integrity.drift.length} account(s) drifting`} testid="integrity-reconciled" />
          <span className="font-mono text-[11px] text-slate-500">{integrity.entries} immutable entries</span>
          <span className="font-mono text-[11px] text-slate-500">treasury ledger {money(integrity.treasury_ledger)}</span>
          {!integrity.reconciled && <span className="font-mono text-[10px] text-amber-400">auto-reconcile runs every 2m</span>}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Revenue note + Policy */}
        <div className="space-y-6">
          <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": "#10B981" }}>
            <Overline>Treasury Funding</Overline>
            <p className="mt-3 text-sm text-slate-300">The treasury is funded by <span className="text-emerald-400 font-medium">real product & order revenue</span> — every paid order books to the authoritative ledger automatically.</p>
            <p className="mt-2 text-[11px] text-slate-500 font-mono">Card-loaded top-ups are disabled by design (stored-value / money-transmission compliance).</p>
          </div>

          <div className="hud-card rounded-lg p-5">
            <div className="flex items-center justify-between">
              <Overline>Approval Policy</Overline>
              {isAdmin && (
                <button data-testid="save-policy-btn" onClick={savePolicy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 font-mono text-[10px] uppercase tracking-wider hover:bg-cyan-500/20 transition-colors">
                  <Save className="w-3 h-3" /> Save
                </button>
              )}
            </div>
            {policy && (
              <div className="mt-4 space-y-3">
                <PolField label="Approval Threshold ($)" hint="Spends above this need human approval" value={policy.approval_threshold} onChange={(v) => setPolicy({ ...policy, approval_threshold: v })} testid="policy-threshold-input" />
                <PolField label="Workspace Daily Cap ($)" value={policy.daily_cap} onChange={(v) => setPolicy({ ...policy, daily_cap: v })} testid="policy-daily-input" />
                <PolField label="Workspace Monthly Cap ($)" value={policy.monthly_cap} onChange={(v) => setPolicy({ ...policy, monthly_cap: v })} testid="policy-monthly-input" />
                <label className="flex items-start gap-3 rounded-md border border-slate-700 bg-[#080B11] px-3 py-2.5 cursor-pointer" data-testid="policy-deploy-gate-row">
                  <input type="checkbox" data-testid="policy-deploy-gate-toggle" checked={!!policy.deploy_requires_approval} disabled={!isAdmin}
                    onChange={(e) => setPolicy({ ...policy, deploy_requires_approval: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-cyan-500" />
                  <span>
                    <span className="block text-sm font-medium text-slate-200">Require approval for production deploys</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">Every live-domain deploy needs a human sign-off — even $0 ones — so untrusted input can't auto-ship code.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 rounded-md border border-slate-700 bg-[#080B11] px-3 py-2.5 cursor-pointer" data-testid="policy-demo-mode-row">
                  <input type="checkbox" data-testid="policy-demo-mode-toggle" checked={!!policy.demo_mode} disabled={!isAdmin}
                    onChange={(e) => setPolicy({ ...policy, demo_mode: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-cyan-500" />
                  <span>
                    <span className="block text-sm font-medium text-slate-200">Demo mode (show "Simulate Sale" shortcut)</span>
                    <span className="block text-[11px] text-slate-500 mt-0.5">Turn off for a real workspace so only genuine, paid orders book revenue — the simulate-sale button disappears.</span>
                  </span>
                </label>
                {isAdmin && (
                  <button data-testid="reset-demo-btn" onClick={resetDemo}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-red-500/40 bg-red-500/5 text-red-400 font-mono text-[10px] uppercase tracking-wider hover:bg-red-500/15 transition-colors">
                    <Ban className="w-3 h-3" /> Reset demo data (wipe simulated orders & balances)
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Approval queue */}
        <div className="hud-card accent-top rounded-lg p-5" style={{ "--accent-line": "#F59E0B" }}>
          <Overline>HITL Approval Queue</Overline>
          <div className="mt-4 space-y-3 max-h-[520px] overflow-y-auto" data-testid="approval-queue">
            {pending.length === 0 && <div className="text-xs text-slate-600 font-mono py-8 text-center">No spends awaiting approval.</div>}
            {pending.map((sp) => (
              <div key={sp.id} className="rounded-md border border-amber-500/20 bg-amber-500/5 p-3" data-testid={`pending-spend-${sp.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold tabular-nums text-amber-400">{money(sp.amount)}</span>
                  <span className="font-mono text-[10px] text-slate-500 uppercase">{sp.category.replace("_", " ")}</span>
                </div>
                <div className="mt-1 text-xs text-slate-300">{sp.agent_name} · {sp.description}</div>
                <div className="mt-1 text-[10px] text-slate-500">{sp.reason}</div>
                {isAdmin ? (
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`approve-${sp.id}`} onClick={() => resolve(sp, true)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 font-mono text-[10px] uppercase tracking-wider hover:bg-emerald-500/25 transition-colors">
                      <Check className="w-3 h-3" /> Approve
                    </button>
                    <button data-testid={`reject-${sp.id}`} onClick={() => resolve(sp, false)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md bg-red-500/15 border border-red-500/40 text-red-400 font-mono text-[10px] uppercase tracking-wider hover:bg-red-500/25 transition-colors">
                      <Ban className="w-3 h-3" /> Reject
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 flex items-center gap-1.5 text-[10px] font-mono text-slate-500"><Lock className="w-3 h-3" /> Admin approval required</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ledger */}
      <div className="hud-card rounded-lg p-5">
        <Overline>Real-Time Ledger</Overline>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
                <th className="pb-2 font-medium">Type</th><th className="pb-2 font-medium">Description</th>
                <th className="pb-2 font-medium text-right">Amount</th><th className="pb-2 font-medium text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              {ledger.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-600 font-mono text-xs">No transactions yet.</td></tr>}
              {ledger.map((t) => {
                const Icon = ICON[t.type] || Coins;
                return (
                  <tr key={t.id} className="border-b border-slate-800/60">
                    <td className="py-2.5"><span className={`inline-flex items-center gap-1.5 font-mono text-[11px] uppercase ${COLOR[t.type]}`}><Icon className="w-3.5 h-3.5" />{t.type}</span></td>
                    <td className="py-2.5 text-slate-300 text-xs">{t.description}</td>
                    <td className={`py-2.5 text-right font-mono tabular-nums ${COLOR[t.type]}`}>{t.type === "spend" ? "-" : "+"}{money(t.amount)}</td>
                    <td className="py-2.5 text-right font-mono tabular-nums text-slate-400">{money(t.balance_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Pill({ ok, label, icon: Icon, testid }) {
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border font-mono text-[10px] uppercase tracking-wider ${ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400" : "border-red-500/30 bg-red-500/10 text-red-400"}`}>
      {Icon ? <Icon className="w-3 h-3" /> : (ok ? <Check className="w-3 h-3" /> : <Ban className="w-3 h-3" />)}
      {label}
    </span>
  );
}

function PolField({ label, hint, value, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] font-semibold tracking-[0.15em] uppercase text-slate-400 block">{label}</label>
      {hint && <span className="text-[10px] text-slate-600">{hint}</span>}
      <input data-testid={testid} type="number" value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-md bg-[#080B11] border border-slate-700 text-slate-100 text-sm font-mono focus:outline-none focus:border-cyan-500/60" />
    </div>
  );
}
